import type { CSSProperties, ReactElement, ReactNode } from "react"
import type { ResponsiveBackgroundImage } from "@onestore/hel/dist/typings/types"
import { CustomTextColors } from "@gatsby-plugin-generic-page/fragments/customColor"
import { CardTariff } from "@ionos-web-design-system/react"
import UdsThemeProvider from "~/components/UdsThemeProvider"
import isEmpty from "~/lib/isEmpty"

type TrialProductBoxProps = {
  title: string
  underTitle?: string
  subtitle?: string
  badgeText?: string
  imageBackground: ResponsiveBackgroundImage
  button: ReactNode
  customOnColorText?: CustomTextColors
  children?: ReactNode
}

export default function TrialProductBox({
  title,
  underTitle,
  subtitle,
  badgeText,
  imageBackground,
  button,
  customOnColorText,
  children,
}: TrialProductBoxProps): ReactElement<TrialProductBoxProps> {
  // customOnColorText is the text color: "customLight" needs light text, which
  // is the dark UDS scheme. Anything else keeps the default dark text.
  const textColorScheme =
    customOnColorText === CustomTextColors.ciemny ? "dark" : "light"

  const cardStyle = {
    "--bg-small": `url(${(imageBackground.small ?? imageBackground.large).src})`,
    "--bg-medium": `url(${(imageBackground.medium ?? imageBackground.large).src})`,
    "--bg-large": `url(${imageBackground.large.src})`,
    backgroundPosition: imageBackground.position,
    backgroundSize: imageBackground.isCover ? "cover" : undefined,
  } as CSSProperties

  return (
    // The wrappers must not generate boxes: CardTariff's root is a subgrid
    // item of the products grid and has to stay its direct layout child.
    <div className="contents [&>div]:contents">
      <UdsThemeProvider forceColorScheme={textColorScheme}>
        <CardTariff
          title={title}
          underTitle={underTitle}
          subtitle={subtitle}
          badgeText={badgeText}
          className="overflow-hidden bg-[image:var(--bg-small)] md:bg-[image:var(--bg-medium)] lg:bg-[image:var(--bg-large)]"
          style={cardStyle}
          button={
            <UdsThemeProvider forceColorScheme="light">
              {button}
            </UdsThemeProvider>
          }
        >
          {!isEmpty(children) ? (
            <UdsThemeProvider forceColorScheme="light">
              <div className="bg-white rounded-(--protected-container-rounded) flex flex-col gap-gap-14">
                {children}
              </div>
            </UdsThemeProvider>
          ) : null}
        </CardTariff>
      </UdsThemeProvider>
    </div>
  )
}
